import type { Rule } from 'antd/es/form';
import type { UserType } from '@/pages/POS/types';

export const CCCD_PATTERN = /^\d{12}$/;
export const TAX_CODE_PATTERN = /^\d{10}(-\d{3})?$/;
// VN phone: mobile prefix 03/05/07/08/09 + 8 digits (10 total), or landline
// 02 + 8–9 digits (10–11 total). Rejects all-zero / invalid prefixes like 0000…
const PHONE_PATTERN = /^(0[35789]\d{8}|02\d{8,9})$/;

// EG-265 — luật VN giao dịch vàng: CCCD luôn 12 chữ số, mọi user_type/fulfillment.
export const citizenIdRules: Rule[] = [
  { required: true, message: 'CCCD bắt buộc theo quy định' },
  { pattern: CCCD_PATTERN, message: 'CCCD phải đủ 12 chữ số' },
];

// EG-265 — CCCD trên hoá đơn cũng luôn 12 chữ số (có thể khác citizen_id case mua hộ).
export const buyerCccdRules: Rule[] = [
  { required: true, message: 'CCCD người mua bắt buộc' },
  { pattern: CCCD_PATTERN, message: 'CCCD người mua phải đủ 12 chữ số' },
];

export const customerPhoneRules: Rule[] = [
  { required: true, message: 'Vui lòng nhập số điện thoại' },
  {
    pattern: PHONE_PATTERN,
    message: 'SĐT không hợp lệ (đầu số VN: 03/05/07/08/09, hoặc cố định 02, 10–11 số)',
  },
];

export const customerBirthdayRules: Rule[] = [
  {
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const match = String(value).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (!match) {
        return Promise.reject(new Error('Ngày sinh phải có định dạng DD/MM/YYYY'));
      }
      const day = Number(match[1]);
      const month = Number(match[2]);
      const year = Number(match[3]);
      const date = new Date(year, month - 1, day);
      const isRealDate =
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day;
      if (!isRealDate) {
        return Promise.reject(new Error('Ngày sinh không hợp lệ'));
      }
      if (year < 1900 || date.getTime() > Date.now()) {
        return Promise.reject(new Error('Ngày sinh không hợp lệ'));
      }
      return Promise.resolve();
    },
  },
];

export const customerEmailRules: Rule[] = [
  { type: 'email', message: 'Email không đúng định dạng' },
];

export const invoiceEmailRules: Rule[] = [
  { type: 'email', message: 'Email hoá đơn không đúng định dạng' },
];

export const requiredRule = (label: string): Rule => ({
  required: true,
  message: `Vui lòng nhập ${label}`,
});

// invoice_tax_id branches by user_type.
export const invoiceTaxIdRules: Rule[] = [
  { required: true, message: 'Vui lòng nhập CCCD/MST' },
  ({ getFieldValue }) => ({
    validator: (_, value) => {
      if (!value) return Promise.resolve();
      const userType = getFieldValue('user_type') as UserType | undefined;
      const isCccd = CCCD_PATTERN.test(value);
      const isTaxCode = TAX_CODE_PATTERN.test(value);
      if (userType === 'personal') {
        if (isCccd || isTaxCode) return Promise.resolve();
        return Promise.reject(
          new Error(
            'Phải là CCCD (12 số) hoặc mã số thuế hợp lệ (VD: 0101234567 hoặc 0101234567-001)',
          ),
        );
      }
      if (isTaxCode) return Promise.resolve();
      return Promise.reject(
        new Error('Mã số thuế không đúng định dạng (10 chữ số hoặc XXXXXXXXXX-YYY)'),
      );
    },
  }),
];

export const deliveryDaysRules: Rule[] = [
  { required: true, message: 'Vui lòng nhập số ngày giao hàng' },
  { type: 'number', min: 1, message: 'Hạn giao phải lớn hơn 0' },
];

// All field names that get cleared when user_type toggles.
export const FIELDS_RESET_ON_USER_TYPE_CHANGE = [
  'customer_phone',
  'customer_name',
  'citizen_id',
  'customer_email',
  'customer_address',
  'customer_city_id',
  'customer_ward_id',
  'customer_birthday',
  'description',
  'invoice_same_as_delivery',
  'invoice_name',
  'invoice_tax_id',
  'invoice_address',
  'invoice_email',
  'buyer_name',
  'buyer_cccd',
  'misa_account_object_id',
] as const;
